import { leafTones, oreTones, textureBaseColors, woodTones } from './palette';

export const TEXTURE_SIZE = 16;

export type TexturePattern =
  | 'noise'
  | 'cobble'
  | 'stone_bricks'
  | 'bricks'
  | 'planks'
  | 'log_side'
  | 'log_top'
  | 'grass_top'
  | 'grass_side'
  | 'leaves'
  | 'glass'
  | 'wool'
  | 'metal'
  | 'ore'
  | 'sand'
  | 'water'
  | 'glow'
  | 'plant'
  | 'redstone_dust'
  | 'piston_face'
  | 'machine_front'
  | 'rail';

export interface TextureRecipe {
  pattern: TexturePattern;
  color: number;
  accent?: number;
  alpha?: number;
}

function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let value = Math.imul(state ^ (state >>> 15), 1 | state);
    value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value;
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(text: string): number {
  let hash = 2166136261;
  for (const character of text) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

const clampByte = (value: number) => (value < 0 ? 0 : value > 255 ? 255 : value | 0);

function shift(color: number, delta: number): number {
  const red = clampByte(((color >> 16) & 255) + delta);
  const green = clampByte(((color >> 8) & 255) + delta);
  const blue = clampByte((color & 255) + delta);
  return (red << 16) | (green << 8) | blue;
}

function scale(color: number, factor: number): number {
  const red = clampByte(((color >> 16) & 255) * factor);
  const green = clampByte(((color >> 8) & 255) * factor);
  const blue = clampByte((color & 255) * factor);
  return (red << 16) | (green << 8) | blue;
}

class Pixels {
  readonly data: Uint8ClampedArray;

  constructor(readonly size: number) {
    this.data = new Uint8ClampedArray(size * size * 4);
  }

  set(x: number, y: number, color: number, alpha = 255) {
    if (x < 0 || y < 0 || x >= this.size || y >= this.size) return;
    const offset = (y * this.size + x) * 4;
    this.data[offset] = (color >> 16) & 255;
    this.data[offset + 1] = (color >> 8) & 255;
    this.data[offset + 2] = color & 255;
    this.data[offset + 3] = alpha;
  }

  fill(color: number, alpha = 255) {
    for (let y = 0; y < this.size; y += 1) {
      for (let x = 0; x < this.size; x += 1) this.set(x, y, color, alpha);
    }
  }

  fillNoise(color: number, random: () => number, strength: number, alpha = 255) {
    for (let y = 0; y < this.size; y += 1) {
      for (let x = 0; x < this.size; x += 1) {
        this.set(x, y, shift(color, Math.round((random() - 0.5) * 2 * strength)), alpha);
      }
    }
  }
}

/**
 * レッドストーンのこな。線がどちらへ伸びるかは形（箱）のほうで作るので、
 * ここは粉そのものの色だけを描く。
 */
function drawRedstoneDust(pixels: Pixels, color: number, random: () => number) {
  const size = pixels.size;
  pixels.fillNoise(color, random, 22);
  for (let grain = 0; grain < size * 3; grain += 1) {
    pixels.set(Math.floor(random() * size), Math.floor(random() * size), scale(color, 0.62));
  }
}

/** ピストンが押す面。板の中央に金具の四角が乗った形。 */
function drawPistonFace(pixels: Pixels, color: number, random: () => number) {
  const size = pixels.size;
  pixels.fillNoise(color, random, 13);
  const edge = scale(color, 0.7);
  for (let along = 0; along < size; along += 1) {
    for (const border of [0, 1, size - 2, size - 1]) {
      const tone = shift(edge, Math.round((random() - 0.5) * 12));
      pixels.set(along, border, tone);
      pixels.set(border, along, tone);
    }
  }
  const plate = scale(color, 1.1);
  for (let y = 4; y < size - 4; y += 1) {
    for (let x = 4; x < size - 4; x += 1) {
      pixels.set(x, y, shift(plate, Math.round((random() - 0.5) * 12)));
    }
  }
}

/** ディスペンサーやドロッパーの口。中央に暗い穴を空ける。 */
function drawMachineFront(pixels: Pixels, color: number, random: () => number) {
  pixels.fillNoise(color, random, 15);
  const rim = scale(color, 0.55);
  const hole = scale(color, 0.24);
  for (let y = 5; y <= 10; y += 1) {
    for (let x = 5; x <= 10; x += 1) {
      const onRim = y === 5 || y === 10 || x === 5 || x === 10;
      pixels.set(x, y, onRim ? rim : shift(hole, Math.round((random() - 0.5) * 8)));
    }
  }
}

/** レール。枕木の上に2本の金属が伸びている絵。 */
function drawRail(pixels: Pixels, color: number, random: () => number) {
  const size = pixels.size;
  pixels.fillNoise(scale(color, 0.32), random, 7);
  const tie = scale(color, 0.66);
  for (let y = 0; y < size; y += 1) {
    if (y % 5 !== 1 && y % 5 !== 2) continue;
    for (let x = 2; x < size - 2; x += 1) pixels.set(x, y, shift(tie, Math.round((random() - 0.5) * 14)));
  }
  const metal = shift(color, 58);
  for (let y = 0; y < size; y += 1) {
    for (const x of [3, 4, size - 5, size - 4]) {
      pixels.set(x, y, shift(metal, Math.round((random() - 0.5) * 18)));
    }
  }
}

function drawCobble(pixels: Pixels, color: number, random: () => number) {
  const size = pixels.size;
  const sites: Array<{ x: number; y: number; tone: number }> = [];
  for (let index = 0; index < 7; index += 1) {
    sites.push({
      x: random() * size,
      y: random() * size,
      tone: Math.round((random() - 0.45) * 46),
    });
  }

  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      let best = 0;
      let bestDistance = Infinity;
      let secondDistance = Infinity;
      sites.forEach((site, index) => {
        const distance = (site.x - x - 0.5) ** 2 + (site.y - y - 0.5) ** 2;
        if (distance < bestDistance) {
          secondDistance = bestDistance;
          bestDistance = distance;
          best = index;
        } else if (distance < secondDistance) {
          secondDistance = distance;
        }
      });
      const edge = Math.sqrt(secondDistance) - Math.sqrt(bestDistance) < 0.9;
      const grain = Math.round((random() - 0.5) * 16);
      pixels.set(x, y, shift(color, edge ? -46 + grain : sites[best].tone + grain));
    }
  }
}

function drawStoneBricks(pixels: Pixels, color: number, random: () => number) {
  const size = pixels.size;
  const rowHeight = size / 2;
  pixels.fillNoise(color, random, 12);
  for (let y = 0; y < size; y += 1) {
    const row = Math.floor(y / rowHeight);
    const offset = row % 2 === 0 ? 0 : size / 2;
    for (let x = 0; x < size; x += 1) {
      const seam = y % rowHeight === 0 || (x + offset) % (size / 2) === 0;
      if (seam) pixels.set(x, y, shift(color, -34 + Math.round(random() * 8)));
    }
  }
}

function drawBricks(pixels: Pixels, color: number, random: () => number) {
  const size = pixels.size;
  const rowHeight = size / 4;
  const mortar = shift(scale(color, 1.18), 34);
  pixels.fill(mortar);
  for (let y = 0; y < size; y += 1) {
    const row = Math.floor(y / rowHeight);
    if (y % rowHeight === 0) continue;
    const offset = row % 2 === 0 ? 0 : rowHeight * 2;
    for (let x = 0; x < size; x += 1) {
      if ((x + offset) % (size / 2) === 0) continue;
      pixels.set(x, y, shift(color, Math.round((random() - 0.5) * 22)));
    }
  }
}

function drawPlanks(pixels: Pixels, color: number, random: () => number) {
  const size = pixels.size;
  const plankHeight = size / 4;
  for (let row = 0; row < 4; row += 1) {
    const rowTone = Math.round((random() - 0.5) * 20);
    for (let y = row * plankHeight; y < (row + 1) * plankHeight; y += 1) {
      const edge = y === row * plankHeight;
      for (let x = 0; x < size; x += 1) {
        const grain = random() < 0.14 ? -22 : Math.round((random() - 0.5) * 12);
        pixels.set(x, y, shift(color, edge ? -40 : rowTone + grain));
      }
    }
    const nailColumn = Math.floor(random() * size);
    pixels.set(nailColumn, row * plankHeight + 1, shift(color, -30));
  }
}

function drawLogSide(pixels: Pixels, color: number, random: () => number) {
  const size = pixels.size;
  for (let x = 0; x < size; x += 1) {
    const columnTone = Math.round((random() - 0.5) * 30);
    for (let y = 0; y < size; y += 1) {
      const knot = random() < 0.08 ? -20 : 0;
      pixels.set(x, y, shift(color, columnTone + knot + Math.round((random() - 0.5) * 10)));
    }
  }
}

function drawLogTop(pixels: Pixels, color: number, bark: number, random: () => number) {
  const size = pixels.size;
  const center = (size - 1) / 2;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const distance = Math.hypot(x - center, y - center);
      const ring = Math.sin(distance * 2.1) * 14;
      const border = x === 0 || y === 0 || x === size - 1 || y === size - 1;
      pixels.set(x, y, border ? shift(bark, Math.round((random() - 0.5) * 14)) : shift(color, Math.round(ring + (random() - 0.5) * 10)));
    }
  }
}

function drawGrassSide(pixels: Pixels, dirt: number, grass: number, random: () => number) {
  const size = pixels.size;
  pixels.fillNoise(dirt, random, 22);
  for (let x = 0; x < size; x += 1) {
    const height = 3 + Math.floor(random() * 3);
    for (let y = 0; y < height; y += 1) {
      pixels.set(x, y, shift(grass, Math.round((random() - 0.5) * 26)));
    }
  }
}

function drawLeaves(pixels: Pixels, color: number, random: () => number) {
  const size = pixels.size;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      if (random() < 0.14) {
        pixels.set(x, y, 0, 0);
        continue;
      }
      const depth = random() < 0.3 ? -34 : Math.round((random() - 0.5) * 30);
      pixels.set(x, y, shift(color, depth));
    }
  }
}

function drawGlass(pixels: Pixels, color: number, random: () => number, alpha: number) {
  const size = pixels.size;
  const frame = shift(color, 40);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const border = x === 0 || y === 0 || x === size - 1 || y === size - 1;
      const inner = x === 1 || y === 1 || x === size - 2 || y === size - 2;
      if (border) {
        pixels.set(x, y, frame, 205);
      } else if (inner && random() < 0.3) {
        pixels.set(x, y, frame, 120);
      } else if (x + y === 5 || x + y === 6) {
        pixels.set(x, y, 0xffffff, 96);
      } else {
        pixels.set(x, y, color, alpha);
      }
    }
  }
}

function drawWool(pixels: Pixels, color: number, random: () => number) {
  const size = pixels.size;
  pixels.fillNoise(color, random, 8);
  for (let index = 0; index < size; index += 1) {
    const x = Math.floor(random() * size);
    const y = Math.floor(random() * size);
    const tone = random() < 0.5 ? 13 : -13;
    pixels.set(x, y, shift(color, tone));
    pixels.set((x + 1) % size, y, shift(color, tone));
  }
}

function drawMetal(pixels: Pixels, color: number, random: () => number) {
  const size = pixels.size;
  pixels.fillNoise(color, random, 8);
  for (let index = 0; index < size; index += 1) {
    pixels.set(index, 0, shift(color, 34));
    pixels.set(0, index, shift(color, 26));
    pixels.set(index, size - 1, shift(color, -34));
    pixels.set(size - 1, index, shift(color, -26));
  }
  for (let index = 2; index < size - 2; index += 1) {
    pixels.set(index, 2, shift(color, 18));
    pixels.set(2, index, shift(color, 14));
  }
}

function drawOre(pixels: Pixels, host: number, gem: number, random: () => number) {
  const size = pixels.size;
  pixels.fillNoise(host, random, 16);
  const clusters = 3 + Math.floor(random() * 3);
  for (let index = 0; index < clusters; index += 1) {
    const originX = 1 + Math.floor(random() * (size - 4));
    const originY = 1 + Math.floor(random() * (size - 4));
    const width = 2 + Math.floor(random() * 2);
    const height = 2 + Math.floor(random() * 2);
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        if (random() < 0.2) continue;
        pixels.set(originX + x, originY + y, shift(gem, Math.round((random() - 0.5) * 26)));
      }
    }
  }
}

function drawWater(pixels: Pixels, color: number, random: () => number, alpha: number) {
  const size = pixels.size;
  for (let y = 0; y < size; y += 1) {
    const wave = Math.round(Math.sin(y * 0.9) * 12);
    for (let x = 0; x < size; x += 1) {
      pixels.set(x, y, shift(color, wave + Math.round((random() - 0.5) * 14)), alpha);
    }
  }
}

function drawGlow(pixels: Pixels, color: number, random: () => number) {
  const size = pixels.size;
  pixels.fillNoise(color, random, 12);
  for (let index = 0; index < 10; index += 1) {
    const x = Math.floor(random() * size);
    const y = Math.floor(random() * size);
    pixels.set(x, y, shift(color, 38));
    pixels.set(x, (y + 1) % size, shift(color, 24));
  }
}

function drawPlant(pixels: Pixels, color: number, random: () => number, blossom?: number) {
  const size = pixels.size;
  const stemColor = blossom === undefined ? color : 0x4b7d2b;
  pixels.fill(0, 0);
  for (let stem = 0; stem < (blossom === undefined ? 5 : 2); stem += 1) {
    const baseX = 2 + Math.floor(random() * (size - 4));
    const height = Math.floor(size * (blossom === undefined ? 0.45 + random() * 0.5 : 0.6));
    for (let step = 0; step < height; step += 1) {
      const y = size - 1 - step;
      const x = baseX + (step > height * 0.6 ? (random() < 0.5 ? -1 : 1) : 0);
      pixels.set(x, y, shift(stemColor, Math.round((random() - 0.5) * 30)));
    }
    if (blossom !== undefined) {
      const top = size - height;
      for (let dy = -2; dy <= 1; dy += 1) {
        for (let dx = -2; dx <= 2; dx += 1) {
          if (Math.abs(dx) + Math.abs(dy) > 2) continue;
          pixels.set(baseX + dx, top + dy, shift(blossom, Math.round((random() - 0.5) * 24)));
        }
      }
    }
  }
}

const patternRules: Array<[RegExp, TexturePattern]> = [
  // レッドストーン装置。glowより先に見て、消えたトーチが光らないようにする。
  [/^redstone_dust/, 'redstone_dust'],
  [/^redstone_torch_off$/, 'noise'],
  [/^(soul_)?campfire_fire$/, 'glow'],
  [/^piston_(top|bottom|inner)/, 'piston_face'],
  [/^piston_side$/, 'planks'],
  [/^(dispenser|dropper)_front$/, 'machine_front'],
  [/^(command_block|repeating_command_block|chain_command_block)_front$/, 'machine_front'],
  [/_rail$|^rail$/, 'rail'],
  [/(_log|_stem|_hyphae|_wood)_top$/, 'log_top'],
  [/((_log|_stem|_hyphae|_wood)$|^bamboo_block$)/, 'log_side'],
  [/leaves/, 'leaves'],
  [/(planks|barrel|bookshelf|crafting_table|scaffolding|ladder|composter|beehive|loom|cartography|fletching|smithing)/, 'planks'],
  [/(glass|honey_block|slime_block|tinted)/, 'glass'],
  [/(water|lava)/, 'water'],
  [/(_wool|_carpet|bed$|banner)/, 'wool'],
  [/_ore$/, 'ore'],
  [/(glowstone|sea_lantern|shroomlight|froglight|lantern|torch|magma|redstone_lamp|_lamp$)/, 'glow'],
  [/(iron_block|gold_block|diamond_block|emerald_block|lapis_block|redstone_block|netherite_block|copper|amethyst|raw_\w+_block|bone_block|quartz_block|quartz_pillar|smooth_quartz|purpur|prismarine)/, 'metal'],
  [/(sand$|_sand$|sandstone|gravel|soul_soil|snow|dust)/, 'sand'],
  [/(stone_bricks|deepslate_bricks|deepslate_tiles|quartz_bricks|chiseled|end_stone_bricks)/, 'stone_bricks'],
  [/(bricks|brick$|_tiles$)/, 'bricks'],
  [/(cobble|blackstone$|basalt|tuff|netherrack|end_stone$|_rock)/, 'cobble'],
  [/(grass|fern|flower|sapling|mushroom$|vine|lily|wheat|bamboo$|sugar_cane|seagrass|kelp|azalea$|roots|sprouts)/, 'plant'],
];

const FLOWER = /(poppy|dandelion|orchid|allium|bluet|tulip|daisy|cornflower|lily_of_the_valley|wither_rose|torchflower|_flower$)/;

function guessPattern(name: string): TexturePattern {
  const rule = patternRules.find(([pattern]) => pattern.test(name));
  return rule ? rule[1] : 'noise';
}

function fallbackColor(name: string): number {
  const hash = hashString(name);
  const red = 92 + (hash & 0x4f);
  const green = 92 + ((hash >> 8) & 0x4f);
  const blue = 92 + ((hash >> 16) & 0x4f);
  return (red << 16) | (green << 8) | blue;
}

function woodOf(name: string): string | undefined {
  return Object.keys(woodTones).find((wood) => name.startsWith(`${wood}_`));
}

let colorKeys: string[] | undefined;

/** `polished_blackstone_bricks` → `blackstone` のように、名前に含まれる既知素材の色を借りる。 */
function relatedColorKey(name: string): string | undefined {
  colorKeys ??= Object.keys(textureBaseColors).sort((left, right) => right.length - left.length);
  return colorKeys.find((key) => key.length >= 4 && (name.includes(`${key}_`) || name.includes(`_${key}`)));
}

/** テクスチャ名から、描画パターンと基準色の組み合わせを決める。 */
export function resolveTextureRecipe(name: string): TextureRecipe {
  if (name === 'grass_block_side' || name === 'podzol_side' || name === 'mycelium_side') {
    const grass = name === 'podzol_side' ? 0x8c6b30 : name === 'mycelium_side' ? 0x6f6265 : textureBaseColors.grass_block;
    return { pattern: 'grass_side', color: textureBaseColors.dirt, accent: grass };
  }
  if (name === 'grass_block' || name === 'grass_block_top' || name === 'moss_block' || name === 'mycelium_top') {
    return { pattern: 'grass_top', color: name === 'grass_block_top' ? textureBaseColors.grass_block : textureBaseColors[name] ?? textureBaseColors.moss_block };
  }

  const oreMatch = /^(?:deepslate_)?(\w+)_ore$/.exec(name);
  if (oreMatch && oreTones[oreMatch[1]]) {
    return {
      pattern: 'ore',
      color: name.startsWith('deepslate_') ? textureBaseColors.deepslate : textureBaseColors.stone,
      accent: oreTones[oreMatch[1]],
    };
  }

  const leafMatch = /^(\w+?)_leaves$/.exec(name);
  if (leafMatch) {
    return { pattern: 'leaves', color: leafTones[leafMatch[1]] ?? leafTones.oak };
  }

  const wood = woodOf(name);
  if (wood && /(_log|_stem|_wood|_hyphae)(_top)?$/.test(name)) {
    const tone = woodTones[wood];
    return name.endsWith('_top')
      ? { pattern: 'log_top', color: tone.core, accent: tone.bark }
      : { pattern: 'log_side', color: tone.bark };
  }

  const pattern = guessPattern(name);
  const known = textureBaseColors[name];
  if (known !== undefined) {
    const alpha = pattern === 'glass' ? 34 : pattern === 'water' ? 200 : undefined;
    const accent = pattern === 'plant' && FLOWER.test(name) ? known : undefined;
    return { pattern, color: known, accent, alpha };
  }

  const stripped = name.replace(/_(top|bottom|side|front|end|inner|outer|north|south|east|west|still|flow)$/, '');
  const strippedColor = textureBaseColors[stripped];
  if (strippedColor !== undefined) {
    return { pattern: guessPattern(stripped), color: strippedColor };
  }

  const related = relatedColorKey(stripped);
  if (related) return { pattern, color: textureBaseColors[related] };

  return { pattern, color: fallbackColor(name) };
}

/** 16×16のRGBAピクセルを決定的に生成する。同じ名前なら常に同じ絵になる。 */
export function renderProceduralTexture(name: string, recipe = resolveTextureRecipe(name)): Uint8ClampedArray {
  const pixels = new Pixels(TEXTURE_SIZE);
  const random = mulberry32(hashString(name));
  const { pattern, color, accent } = recipe;

  switch (pattern) {
    case 'cobble':
      drawCobble(pixels, color, random);
      break;
    case 'stone_bricks':
      drawStoneBricks(pixels, color, random);
      break;
    case 'bricks':
      drawBricks(pixels, color, random);
      break;
    case 'planks':
      drawPlanks(pixels, color, random);
      break;
    case 'log_side':
      drawLogSide(pixels, color, random);
      break;
    case 'log_top':
      drawLogTop(pixels, color, accent ?? shift(color, -40), random);
      break;
    case 'grass_top':
      pixels.fillNoise(color, random, 26);
      break;
    case 'grass_side':
      drawGrassSide(pixels, color, accent ?? textureBaseColors.grass_block, random);
      break;
    case 'leaves':
      drawLeaves(pixels, color, random);
      break;
    case 'glass':
      drawGlass(pixels, color, random, recipe.alpha ?? 96);
      break;
    case 'wool':
      drawWool(pixels, color, random);
      break;
    case 'metal':
      drawMetal(pixels, color, random);
      break;
    case 'ore':
      drawOre(pixels, color, accent ?? oreTones.iron, random);
      break;
    case 'sand':
      pixels.fillNoise(color, random, 20);
      break;
    case 'water':
      drawWater(pixels, color, random, recipe.alpha ?? 200);
      break;
    case 'glow':
      drawGlow(pixels, color, random);
      break;
    case 'plant':
      drawPlant(pixels, color, random, accent);
      break;
    case 'redstone_dust':
      drawRedstoneDust(pixels, color, random);
      break;
    case 'piston_face':
      drawPistonFace(pixels, color, random);
      break;
    case 'machine_front':
      drawMachineFront(pixels, color, random);
      break;
    case 'rail':
      drawRail(pixels, color, random);
      break;
    default:
      pixels.fillNoise(color, random, 18);
      break;
  }

  return pixels.data;
}
